import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { useSelector } from "react-redux";
import { getPusherClient } from "@/lib/services/pusher-client";

export const usePusherEnrichment = (
  onEnrichmentComplete,
  onEnrichmentFailed
) => {
  const { data: session } = useSession();
  const channelRef = useRef(null);
  const pusherClientRef = useRef(null);
  const hasConnectedRef = useRef(false);

  const needsEnrichment = useSelector((state) => state.user.needsEnrichment);
  const enrichmentStatus = useSelector((state) => state.user.enrichmentStatus);

  useEffect(() => {
    const shouldConnect = needsEnrichment && enrichmentStatus !== "completed";

    if (
      !shouldConnect ||
      !session?.user?.sub ||
      !onEnrichmentComplete ||
      hasConnectedRef.current
    ) {
      return;
    }

    hasConnectedRef.current = true;

    const userSub = session.user.sub;
    pusherClientRef.current = getPusherClient();

    if (!pusherClientRef.current) {
      return;
    }

    const pusher = pusherClientRef.current;
    const channelName = `private-user-${userSub}`;
    channelRef.current = pusher.subscribe(channelName);

    const handleEnrichmentComplete = (data) => {
      onEnrichmentComplete(data);
    };

    const handleEnrichmentFailed = (data) => {
      if (onEnrichmentFailed) {
        onEnrichmentFailed(data);
      }
    };

    channelRef.current.bind("enrichment-completed", handleEnrichmentComplete);
    channelRef.current.bind("enrichment-failed", handleEnrichmentFailed);

    return () => {
      if (channelRef.current) {
        channelRef.current.unbind(
          "enrichment-completed",
          handleEnrichmentComplete
        );
        channelRef.current.unbind("enrichment-failed", handleEnrichmentFailed);

        if (pusherClientRef.current) {
          pusherClientRef.current.unsubscribe(channelName);
        }
      }
    };
  }, [
    session?.user?.sub,
    onEnrichmentComplete,
    onEnrichmentFailed,
    needsEnrichment,
    enrichmentStatus,
  ]);

  return {
    isConnected: channelRef.current?.pusher?.connection?.state === "connected",
    channelName: session?.user?.sub ? `private-user-${session.user.sub}` : null,
  };
};
